import { $gettext } from './gettext';
import parseOptions from './parse_options.js';
import extractCallback from './extract_callback.js';
import Overlay from './overlay.js';
import PageLayout from './page_layout.js';
import Report from './report.ts';

/**
 * Specialized dialog handler
 *
 * @author      Jan-Hendrik Willms <tleilax+studip@gmail.com>
 * @version     1.0
 * @since       Stud.IP 3.1
 * @license     GLP2 or any later version
 * @copyright   2014 Stud.IP Core Group
 * @todo        Handle file uploads <http://goo.gl/PnSra8>
 */
let dialog_margin = 0;

/**
 * Extract buttons from given element.
 *
 * @param {Element} element
 * @param {function|null} callback
 */
function extractButtons(element, callback = null) {
    const buttons = [];
    $('[data-dialog-button]', element)
        .hide()
        .find('a,button')
        .addBack()
        .filter('a,button')
        .each(function() {
            const id = $(this).uniqueId().attr('id');
            const label = $(this).text();
            const cancel = $(this).is('.cancel');
            const index = cancel ? 'cancel' : label;
            let classes = $(this).attr('class') || '';
            const name = $(this).attr('name') || '';
            const disabled = $(this).is(':disabled');

            classes = classes.replace(/\bbutton\b/, '').trim();

            buttons.push({
                id: id,
                text: label,
                class: classes,
                name: name,
                disabled: disabled,
                click: () => this.click()
            });

            if (callback !== null) {
                callback(this, index);
            }
        });

    return buttons;
}

const Dialog = {
    instances: {},
    stack: [],
    hasInstance(id = 'default') {
        return this.instances[id] !== undefined;
    },
    getInstance(id = 'default') {
        if (!this.hasInstance(id)) {
            this.instances[id] = {
                open: false,
                fixedDimensions: false,
                element: $('<div>'),
                options: {},
                previous: this.stack[0] || false
            };

            this.stack.unshift(id);
        }
        return this.instances[id];
    },
    removeInstance(id = 'default') {
        if (this.hasInstance(id)) {
            delete this.instances[id];

            const index = this.stack.indexOf(id);
            this.stack.splice(index, 1);
        }
    },

    // Creates a dialog from an anchor, a button or a form element.
    // Will update the dialog if it is already open
    fromElement(element, options = {}) {
        if ($(element).is(':disabled') || !Dialog.shouldOpen()) {
            return;
        }

        if (options.close) {
            Dialog.close(options);
            return;
        }

        if (!$(element).is('a,button,form,input[type=image],input[type=submit]')) {
            throw 'Dialog.fromElement called on an unsupported element.';
        }

        options.origin = element;
        options.title =
            options.title ||
            Dialog.getInstance(options.id).options.title ||
            $(element).attr('title') ||
            $(element).find('[title]').first().attr('title') ||
            $(element).filter('a,button').text();
        options.method = 'get';
        options.data = {};

        let url;
        let fd;

        // Predefine options
        if ($(element).is('form,button,input')) {
            url = $(element).attr('formaction') ||
                $(element).closest('form').data('formaction') ||
                $(element).closest('form').attr('action');
            options.method = $(element).closest('form').attr('method');
            options.data = $(element).closest('form').serializeArray();

            if ($(element).is('button,input')) {
                options.data.push({
                    name: $(element).attr('name'),
                    value: $(element).val()
                });
            } else if ($(element).data().triggeredBy) {
                options.data.push($(element).data().triggeredBy);
            }
            $(element).closest('form').removeData('formaction');

            if ($(element).closest('form').attr('enctype') === 'multipart/form-data') {
                options.processData = false;

                fd = new FormData();
                options.data.forEach(function(item) {
                    fd.append(item.name, item.value);
                });

                $(element).closest('form').find('input[type=file]').each(function() {
                    let name = $(this).attr('name');
                    for (let i = 0; i < this.files.length; i += 1) {
                        fd.append(name, this.files[i]);
                    }
                });

                options.data = fd;
            }
        } else {
            url = $(element).attr('href');
        }

        return Dialog.fromURL(url, options);
    },

    // Creates a dialog from a passed url
    fromURL(url, options = {}) {
        // Append overlay
        if (Dialog.getInstance(options.id).open) {
            Overlay.show(true, Dialog.getInstance(options.id).element.parent());
        } else {
            Overlay.show(true);
        }

        // Send ajax request
        $.ajax({
            url: url,
            type: (options.method || 'get').toUpperCase(),
            data: options.data || {},
            headers: { 'X-Dialog': true },
            cache: false,
            contentType:
                options.processData !== undefined && !options.processData
                    ? false
                    : 'application/x-www-form-urlencoded; charset=UTF-8',
            processData: options.processData ?? true
        })
            .done(function(response, status, xhr) {
                let advance = true;

                // Trigger event
                $(options.origin || document).trigger('dialog-load', { xhr: xhr, options: options });

                // Execute all defined header handlers
                const handlers = Object.assign(
                    Dialog.handlers.header,
                    STUDIP.Dialog.handlers.header
                );
                $.each(handlers, (header, handler) => {
                    let value = xhr.getResponseHeader(header);
                    let result = true;
                    if (value !== null) {
                        result = handler(value, options, xhr);
                    }
                    advance = advance && result !== false;
                    return result;
                });

                Overlay.hide(0);

                if (advance) {
                    Dialog.show(response, options);
                }
            })
            .fail((jqXHR, textStatus, errorThrown) => {
                Report.error($gettext('Es ist ein Fehler aufgetreten.'), jqXHR.responseJSON?.message ?? errorThrown);
                Overlay.hide();

            });

        return true;
    },

    // Opens or updates the dialog
    show(content, options = {}) {
        options = Object.assign({}, Dialog.options, options);

        options.wikilink = options.wikilink === undefined ? true : options.wikilink;

        const scripts = $('<div>' + content + '</div>').filter('script'); // Extract scripts
        let dialog_options = {};
        const instance = Dialog.getInstance(options.id);

        if (instance.open) {
            options.title = options.title || instance.element.dialog('option', 'title');
        }

        if (options['center-content']) {
            content = '<div class="studip-dialog-centered-helper">' + content + '</div>';
        }

        // Hide and update container
        instance.element.hide().html(content);

        // Store options and dimensions
        instance.options = options;
        instance.dimensions = Dialog.calculateDimensions(instance, content, options);
        instance.previous_title = instance.previous_title || PageLayout.title;

        // Set dialog options
        dialog_options = $.extend(dialog_options, {
            width: instance.dimensions.width,
            height: instance.dimensions.height,
            dialogClass: Dialog.getClasses(options),
            classes: {
                'ui-dialog-content': 'studip-dialog-content',
            },
            buttons: options.buttons || {},
            title: options.title,
            modal: true,
            resizable: options.resize ?? true,
            create(event) {
                $(event.target)
                    .parent()
                    .css('position', 'fixed');
            },
            resizeStop(event, ui) {
                const position = [
                    Math.floor(ui.position.left) - $(window).scrollLeft(),
                    Math.floor(ui.position.top) - $(window).scrollTop()
                ];
                $(event.target)
                    .parent()
                    .css('position', 'fixed');
                $(event.target).dialog('option', 'position', position);

                instance.fixedDimensions = true;
                instance.dimensions = ui.size;
            },
            open(event) {
                PageLayout.title = dialog_options.title;

                // Prevent tooltips from being automatically focussed
                if (event.target.querySelector('[autofocus]') === null) {
                    $(':tabbable.tooltip:focus').data('tooltipObject')?.hide();
                    $(':tabbable:not(.tooltip):first', event.target).trigger('focus');
                }

                // Adjust titlebar of dialog
                const helpbar_element = $('.helpbar a[href*="hilfe.studip.de"]');
                const tooltip = helpbar_element.text();
                const link = options.wiki_link || helpbar_element.attr('href');
                const element = $('<a class="ui-dialog-titlebar-wiki"' + ' target="_blank" rel="noopener noreferrer">')
                    .attr('href', link)
                    .attr('title', tooltip);

                if (options.wikilink) {
                    $(this)
                        .siblings('.ui-dialog-titlebar')
                        .addClass('with-wiki-link')
                        .find('.ui-dialog-titlebar-close')
                        .before(element);
                }

                $(this).parent().find('.ui-dialog-title').attr({
                    title: options.title,
                    role: 'heading',
                    'aria-level': 2
                });
                $(this).parents('.studip-dialog').attr('aria-modal', 'true');

                instance.open = true;
                // Execute scripts
                $('head').append(scripts);

                $(options.origin || document).trigger('dialog-open', {dialog: this, options: options});
            },
            close() {
                $(options.origin || document).trigger('dialog-close', {dialog: this, options: options});

                PageLayout.title = instance.previous_title;

                Dialog.close(options);
            }
        });

        // Create buttons
        if (
            options.buttons === undefined
            || (options.buttons && !$.isPlainObject(options.buttons))
        ) {
            // Create observer to detect changes on disabled attribute
            const observer = new MutationObserver((mutations) => {
                mutations.forEach(mutation => {
                    const id = mutation.target.id;
                    const buttonIndex = dialog_options.buttons.findIndex(button => button.id === id);

                    if (mutation.attributeName === 'disabled') {
                        dialog_options.buttons[buttonIndex].disabled = mutation.target.disabled;
                    } else if (mutation.attributeName === 'class') {
                        const classes = mutation.target.classList.toString();
                        dialog_options.buttons[buttonIndex].class = classes.replace(/\bbutton\b/, '');
                    }

                    instance.element.dialog('option', 'buttons', dialog_options.buttons);
                });
            });

            dialog_options.buttons = extractButtons(instance.element, (button) => {
                observer.observe(button, {
                    attributes: true,
                    attributeFilter: ['class', 'disabled'],
                });
            });

            // Create 'close' button
            const cancelButton = dialog_options.buttons.find(button => button.class.split(' ').includes('cancel'));
            if (!cancelButton) {
                dialog_options.buttons.push({
                    text: $gettext('Schließen'),
                    class: 'cancel',
                    click: () => Dialog.close(options),
                });
            } else {
                cancelButton.click = () => Dialog.close(options);
            }
        }

        // Create/update dialog
        instance.element.dialog(dialog_options);
        instance.element.scrollTo(0, 0);

        // Trigger update event on document since options.origin might have been removed
        $(document).trigger('dialog-update', { dialog: instance.element, options: options });
    },

    // Closes the dialog for good
    close(options = {}) {
        if (Dialog.hasInstance(options.id)) {
            const instance = Dialog.getInstance(options.id);

            if (instance.open) {
                instance.open = false;
                try {
                    instance.element.dialog('close');
                    instance.open = instance.element.dialog('isOpen');
                } catch {
                    // No action necessary
                }

                // Apparently the close event has been canceled, so don't force
                // a close
                if (instance.open) {
                    return false;
                }

                try {
                    instance.element.dialog('destroy');
                    instance.element.remove();
                } catch {
                    // No action necessary
                }
            }

            Dialog.removeInstance(options.id);
        }

        if (options['reload-on-close'] && options['is-reloading'] === undefined) {
            window.location.reload();
            options['is-reloading'] = true;
        }
    },

    getClasses(options) {
        const classes = ['studip-dialog'];

        if (options.dialogClass) {
            classes.push(options.dialogClass);
        } else if (options['center-content']) {
            classes.push('studip-dialog-centered');
        }

        return classes.join(' ');
    },

    calculateDimensions(instance, content, options) {
        const previous = instance.previous !== false ? Dialog.getInstance(instance.previous) : false;
        let width = options.width || ($(window).width() * 2) / 3;
        let height = options.height || ($(window).height() * 2) / 3;
        let max_width = $(window).width() * 0.95;
        let max_height = $(window).height() * 0.9;
        let helper;
        let temp;

        if (instance.fixedDimensions) {
            return instance.dimensions;
        }

        if ($('html').is('.responsive-display')) {
            max_width  = $(window).width() - 6; // Subtract border
            max_height = $(window).height();

            if (options.width === undefined) {
                width  = $(window).width() * 0.95;
                height = $(window).height() * 0.98;
            }
        }

        // Adjust size if neccessary
        if (!options.size) {
            width  = instance.dimensions?.width ?? width;
            height = instance.dimensions?.height ?? height;
        } else if (options.size === 'auto' || options.size === 'fit') {
            // Render off screen
            helper = $('<div class="ui-dialog ui-widget ui-widget-content">');
            helper.addClass(Dialog.getClasses(options));

            const helper_title = $('<span class="ui-dialog-title">')
                .text(options.title)
                .appendTo(helper)
                .wrap('<div class="ui-dialog-titlebar ui-helper-clearfix">')
                .after('<button class="ui-button ui-button-icon-only ui-dialog-titlebar-close">close</button>');
            if (options.wikilink) {
                helper_title.parent().append('<a class="ui-dialog-titlebar-wiki"></a>').addClass('with-wiki-link');
            }


            $('<div class="ui-dialog-content">').html($.parseHTML(content)).appendTo(helper);
            // Prevent buttons from wrapping
            $('[data-dialog-button]', helper).css('white-space', 'nowrap');
            // Add cancel button if missing
            if ((options.buttons === undefined || options.buttons !== false)) {
                $('<div class="ui-dialog-buttonpane ui-widget-content ui-helper-clearfix"></div>')
                    .append('<div class="ui-dialog-buttonset"><button class="ui-button ui-widget ui-corner-all cancel">Foo</button></div>')
                    .appendTo(helper)
            }

            helper.css({
                position: 'absolute',
                left: '-10000px',
                top: '-10000px',
                width: 'auto'
            }).appendTo('body');

            // Calculate width and height
            width = Math.min(helper.outerWidth(true) + dialog_margin, max_width);
            height = Math.min(helper.outerHeight(true), max_height);

            if (options.size === 'auto') {
                width = Math.max(300, width);
                height = Math.max(200, height);
            }
            // Remove helper element
            helper.remove();
        } else if (options.size === 'big') {
            width = $(window).width() * 0.9;
            height = $(window).height() * 0.8;
        } else if (options.size === 'medium') {
            width = $(window).width() * 0.6;
            height = $(window).height() * 0.5;
        } else if (options.size === 'medium-43') {
            //Medium size in 4:3 aspect ratio
            height = $(window).height() * 0.8;
            width = parseInt(height) * 1.33333333;
            if (width > $(window).width()) {
                width = $(window).width() * 0.9;
            }
        } else if (options.size === 'small') {
            width = 300;
            height = 200;
        } else if (options.size.match(/^\d+x\d+$/)) {
            temp = options.size.split('x');
            width = temp[0];
            height = temp[1];
        } else if (!options.size.match(/\D/)) {
            width = height = options.size;
        }

        // Ensure dimensions fit in viewport
        width = Math.min(width, max_width);
        height = Math.min(height, max_height);
        if (
            previous &&
            previous.dimensions !== undefined &&
            width > previous.dimensions.width &&
            height > previous.dimensions.height
        ) {
            width = width > previous.dimensions.width ? previous.dimensions.width * 0.95 : width;
            height = height > previous.dimensions.height ? previous.dimensions.height * 0.95 : height;
        }

        return {
            width: width,
            height: height
        };
    },

    // Specialized confirmation dialog
    confirm(question, yes_callback, no_callback) {
        return $.Deferred(function(defer) {
            if (question === true) {
                defer.resolve();
            } else if (question === false) {
                defer.reject();
            } else {
                Dialog.show(_.escape(question).replace("\n", '<br>'), {
                    id: 'confirmation-dialog',
                    title: $gettext('Bitte bestätigen Sie die Aktion'),
                    size: 'fit',
                    wikilink: false,
                    dialogClass: 'studip-confirmation',
                    buttons: {
                        accept: {
                            text: $gettext('Ja'),
                            click: defer.resolve,
                            class: 'accept'
                        },
                        cancel: {
                            text: $gettext('Nein'),
                            click: defer.reject,
                            class: 'cancel'
                        }
                    }
                });
            }
            $(document).one('dialog-close', function() {
                if (defer.state() === 'pending') {
                    defer.reject();
                }
            });
        })
            .then(yes_callback, no_callback)
            .always(function() {
                Dialog.close({ id: 'confirmation-dialog' });
            })
            .promise();
    },

    confirmAsPost(question, action) {
        const form = $('<form/>', {
            action: action,
            method: 'post'
        });
        $('<input/>', {
            type: 'hidden',
            name: STUDIP.CSRF_TOKEN.name,
            value: STUDIP.CSRF_TOKEN.value
        }).appendTo(form);

        $('body').append(form);

        Dialog.confirm(question).done(() => {
            form.submit();
        });

        return false;
    },

    registerHeaderHandler(header, handler) {
        Dialog.handlers.header[header] = handler;
    },
    removeHeaderHandler(header) {
        if (Dialog.handlers.header[header] !== undefined) {
            delete Dialog.handlers.header[header];
        }
    },

    initialize() {
        function checkValidity(element) {
            if (element.matches('a')) {
                return true;
            }

            const form = element.closest('form');
            if (form === null) {
                return true;
            }
            return form.checkValidity();
        }

        // Actual dialog handler
        function dialogHandler(event) {
            if (!event.isDefaultPrevented() && checkValidity(event.currentTarget)) {
                let target = $(event.target).closest('[data-dialog]');
                let options = target.data().dialog;

                if (
                    target.is('form')
                    && event.originalEvent?.submitter
                    && $(event.originalEvent.submitter).attr('formaction')
                ) {
                    target.data('formaction', $(event.originalEvent.submitter).attr('formaction'));
                }

                if (Dialog.fromElement(target, parseOptions(options))) {
                    event.preventDefault();
                }
            }
        }

        function clickHandler(event) {
            if (!event.isDefaultPrevented() && checkValidity(event.currentTarget)) {
                const element = $(event.target).closest(':submit,input[type="image"]');
                const form = element.closest('form');
                const action = element.attr('formaction');
                form.data('triggeredBy', {
                    name: $(event.target).attr('name'),
                    value: $(event.target).val()
                });
                if (action) {
                    form.data('formaction', action);
                }
            }
        }

        // Calculate dialogs margins (outer width - inner width of the dialog) in
        // order to properly calculated needed dialog widths. Otherwise horizontal
        // scrollbars will occur. This is located here because it is only
        // used in Dialog.show().
        const temp = $('<div class="ui-dialog" style="position: absolute;left:-1000px;top:-1000px;"></div>');
        temp.html('<div class="ui-dialog-content ui-widget-content"><div style="width: 100%">foo</div></div>');
        temp.appendTo('body');
        dialog_margin = temp.outerWidth(true) - $('.ui-dialog-content', temp).width();
        temp.remove();

        // Handle links, buttons and forms
        $(document)
            .on(
                'click',
                'a[data-dialog],button[data-dialog],input[type=image][data-dialog],input[type=submit][data-dialog]',
                dialogHandler
            )
            .on('click', 'form[data-dialog] :submit', clickHandler)
            .on('click', 'form[data-dialog] input[type=image]', clickHandler)
            .on('submit', 'form[data-dialog]', dialogHandler);

        // Close dialog on click outside of it
        $(document).on('click', '.ui-widget-overlay', function() {
            if ($('.ui-dialog').length > 0 && Dialog.stack.length) {
                Dialog.close({
                    id: Dialog.stack[0]
                });
            }
        });

        // Recalculate dialog dimensions upon window resize. This is throttled
        // since the resize event keeps on firing during the resizing.
        let timeout = null;
        $(window).on('resize', (event) => {
            if (event.target !== window) {
                return;
            }

            clearTimeout(timeout);
            timeout = setTimeout(() => {
                Dialog.stack.forEach((id) => {
                    const instance = Dialog.getInstance(id);
                    instance.dimensions = Dialog.calculateDimensions(
                        instance,
                        $(instance.element).html(),
                        instance.options
                    );

                    $(instance.element).dialog('option', 'width', instance.dimensions.width);
                    $(instance.element).dialog('option', 'height', instance.dimensions.height);
                });
            }, 10);
        });
    },

    /**
     * legacy method, remove in future
     * @return boolean
     */
    shouldOpen() {
        return true;
//        return !$('html').is('.responsive-display') && $(window).innerHeight() >= 400;
    },
    handlers: {
        header: {}
    }
};

// Handler for HTTP header X-Location: Relocate to another location
Dialog.registerHeaderHandler('X-Location', (location, options) => {
    location = decodeURI(location);

    if (document.location.href === location) {
        document.location.reload(true);
    } else {
        $(window)
            .on('hashchange', function() {
                document.location.reload(true);
            })
            .on('beforeunload', function() {
                $(window).off('hashchange');
            });
    }

    Dialog.close(options);
    document.location = location;

    return false;
});

// Handler for HTTP header X-Dialog-Execute: Execute arbitrary function
Dialog.registerHeaderHandler('X-Dialog-Execute', (value, options, xhr) => {
    let callback = window;
    const payload = xhr.getResponseHeader('Content-Type').match(/json/)
        ? JSON.parse(xhr.responseText)
        : xhr.responseText;

    // Try to parse value as JSON (value might be {func: 'foo', payload: {}})
    try {
        value = JSON.parse(value);
    } catch {
        value = { func: value };
    }

    // Check for invalid call
    if (value.func === undefined) {
        throw 'Dialog: Invalid value for X-Dialog-Execute';
    }

    // Populate payload if not set
    if (value.payload === undefined) {
        value.payload = xhr.getResponseHeader('Content-Type').match(/json/)
            ? JSON.parse(xhr.responseText)
            : xhr.responseText;
    }

    // Find callback
    callback = extractCallback(value.func, payload);

    // Check callback
    if (typeof callback !== 'function') {
        throw 'Dialog: Given callback is not a valid function';
    }

    // Execute callback
    return callback(value.payload, xhr);
});

// Handler for HTTP header X-Dialog-Close: Close the dialog
Dialog.registerHeaderHandler('X-Dialog-Close', (value, options) => {
    Dialog.close(options);
    return false;
});

// Handler for HTTP header X-Wikilink: Set the options' wiki link
Dialog.registerHeaderHandler('X-Wikilink', (link, options)=> {
    options.wiki_link = link;
});

// Handler for HTTP header X-Title: Set the dialog title
Dialog.registerHeaderHandler('X-Title', (title, options) => {
    title = decodeURIComponent(title);
    if (title !== $('title').data().original) {
        options.title = title || options.title;
    }
});

// Handler for HTTP header X-No-Buttons: Decide whether to show dialog buttons
Dialog.registerHeaderHandler('X-No-Buttons', (value, options) => {
    options.buttons = false;
    options.dialogClass = 'no-default-buttons';
});

// Handler for HTTP header X-Dialog-Size: Adjust the size of the dialog
Dialog.registerHeaderHandler('X-Dialog-Size', (value, options) => {
    options.size = value;
});

export default Dialog;
